import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Input, Button, Form, Select, message, InputNumber, Image, Modal } from "antd";
import { RcFile } from "antd/es/upload/interface";
import { ApolloError } from "@apollo/client";

import { Product_Attachments_Insert_Input, Products, Maybe } from "graphql/generated/graphql";
import { useGetProductsByOrganizationIdQuery } from "graphql/queries/get-products-by-organization-id.generated";
import { useProductCategories, ProductCategory } from "hooks/useProductCategories";
import { useUpdateProductByIdMutation } from "graphql/mutations/update-product-by-id.generated";
import { useAddProductAttachmentsMutation } from "graphql/mutations/add-attachments.generated";
import { useDeleteAttachmentByIdMutation } from "graphql/mutations/delete-attachment-by-id.generated";

import AddAttachmentModal from "components/add-attachment-modal/add-attachment-modal";
import AttachmentItem from "components/attachment-item/attachment-item";
import UploadPhotoModal from "components/upload-photo-modal/upload-photo-modal";

import { useSelectedAttachments, clearSelectedAttachments } from "state/reactive-vars/selected-attachment-var";
import type { DetectedDevice } from "hooks/useDeviceDetectionCore";
import { createFirebaseFileName, uploadToStorage } from "utils/files-management";

const { TextArea } = Input;

interface NewProductModalProps {
	open: boolean;
	onCancel: () => void;
	onSuccess?: () => void;
	onClickAttachment?: (file: any) => void;
	detectedDevice?: DetectedDevice | null;
	existingProduct?: Products | null; // For editing existing products
	/** Callback to update the detected device in the parent's local state (device detection flow only) */
	onUpdateProduct?: (draft_product_id: string, updates: Partial<DetectedDevice>) => Promise<void>;
	/** Callback to add a brand-new product (no pre-existing detected device) */
	onAddProduct?: (product: Partial<DetectedDevice>) => Promise<void>;
}

// Unified form state interface
interface ProductFormState {
	name: string;
	description: string;
	productNotes: string;
	sku: string;
	type: ProductCategory | null;
	model: string;
	make: string;
	retailPrice: number | null;
	productPhotoThumbUrl: string;
	productPhotoFile: RcFile | null;
	attachmentsFiles: Array<Product_Attachments_Insert_Input | any>;
}

// Initial form state
const INITIAL_FORM_STATE: ProductFormState = {
	name: "",
	description: "",
	productNotes: "",
	sku: "",
	type: null,
	model: "",
	make: "",
	retailPrice: null,
	productPhotoThumbUrl: "",
	productPhotoFile: null,
	attachmentsFiles: []
};

/**
 * Helper function to populate form state from a Product
 */
const getFormStateFromProduct = (product: Products): Partial<ProductFormState> => {
	return {
		make: product.make || "",
		model: product.model || "",
		name: product.name || "",
		description: product.description || "",
		productNotes: product.product_notes || "",
		sku: product.sku || "",
		type: product.product_category || null,
		retailPrice: product.retail_price || null,
		attachmentsFiles:
			product.product_attachments && product.product_attachments.length > 0 ? product.product_attachments : [],
		productPhotoThumbUrl: product.photo_link || ""
	};
};

/**
 * Helper function to populate form state from a DetectedDevice
 */
const getFormStateFromDetectedDevice = (device: DetectedDevice): Partial<ProductFormState> => {
	return {
		make: device.make || "",
		model: device.model || "",
		name: device.name || "",
		description: device.description || "",
		productNotes: device.product_notes || "",
		sku: device.sku || "",
		type: device.type || null,
		retailPrice: device.retail_price || null,
		attachmentsFiles: device.attachments && device.attachments.length > 0 ? device.attachments : [],
		productPhotoThumbUrl: device.photo_link || "",
		productPhotoFile: device.productPhotoFile || null
	};
};

const NewProductModal: React.FC<NewProductModalProps> = ({
	open,
	onCancel,
	onSuccess,
	onClickAttachment,
	detectedDevice,
	existingProduct,
	onUpdateProduct,
	onAddProduct
}) => {
	const { updateSelectedAttachments } = useSelectedAttachments();

	const [productOptions, setProductOptions] = useState<Array<Products>>([]);
	const [selectedProduct, setSelectedProduct] = useState<Maybe<Products>>(null);

	// Unified form state - single source of truth
	const [formState, setFormState] = useState<ProductFormState>(INITIAL_FORM_STATE);

	// Get product categories using the custom hook
	const { categoryOptions, getCategoryFromName, categoryMap } = useProductCategories();

	const [uploading, setUploading] = useState(false);
	const [addAttachmentModalOpen, setAddAttachmentModalOpen] = useState<boolean>(false);
	const [addAttachmentModalEditMode, setAddAttachmentModalEditMode] = useState<boolean>(false);
	const [addProductPhotoModalOpen, setAddProductPhotoModalOpen] = useState<boolean>(false);

	// Determine if we're in edit mode
	const isEditMode = !!existingProduct;

	// True when the product's current category is a raw AI value (e.g. "gate_operator")
	// that doesn't exist in the DB-backed categoryMap. In that case the Device Type select
	// is enabled even when selectedProduct is set, so the user can fix it.
	const typeNotInMap = !!formState.type && !categoryMap.has(formState.type.name);

	// Update product mutation for edit mode
	const [updateProductByIdMutation, { loading: loadingUpdate }] = useUpdateProductByIdMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onCompleted: () => {
			message.success("Product updated successfully");
			resetForm();
			if (onSuccess) onSuccess();
			onCancel();
		},
		onError: (error: ApolloError) => {
			console.error("updateProductByIdMutation Error:", error);
			message.error("Failed to update product");
		},
		refetchQueries: ["getHandoverById", "getProductById", "getProducts"]
	});

	// Add attachments mutation
	const [addProductAttachmentsMutation, { loading: loadingAddAttachment }] = useAddProductAttachmentsMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		refetchQueries: ["getHandoverById", "getProductById", "getProducts"]
	});

	// Delete attachment mutation
	const [deleteAttachmentByIdMutation, { loading: loadingDeleteAttachment }] = useDeleteAttachmentByIdMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		refetchQueries: ["getHandoverById", "getProductById", "getProducts"]
	});

	/**
	 * Generic function to update form state
	 * This replaces all individual setters
	 */
	const updateFormState = useCallback((updates: Partial<ProductFormState>) => {
		setFormState(prev => ({ ...prev, ...updates }));
	}, []);

	/**
	 * Reset form to initial state
	 */
	const resetForm = useCallback(() => {
		setFormState(INITIAL_FORM_STATE);
		setSelectedProduct(null);
	}, []);

	/**
	 * Populate form from product data
	 */
	const populateFormFromProduct = useCallback(
		(product: Products) => {
			const updates = getFormStateFromProduct(product);
			updateFormState(updates);
		},
		[updateFormState]
	);

	/**
	 * Populate form from detected device
	 */
	const populateFormFromDetectedDevice = useCallback(
		(device: DetectedDevice) => {
			const updates = getFormStateFromDetectedDevice(device);
			updateFormState(updates);
		},
		[updateFormState]
	);

	const handleOnCompleteGetProducts = useCallback(
		data => {
			const { products } = data;

			const options = products.map(element => {
				return { value: JSON.stringify(element), label: element.name };
			});

			setProductOptions(options);

			// If there's a detected device with an ID, populate from matching product
			if (detectedDevice?.id) {
				const selectedProduct = products.find(element => element.id === detectedDevice?.id);
				if (selectedProduct) {
					setSelectedProduct(selectedProduct);
					populateFormFromProduct(selectedProduct);
				}
			}
		},
		[detectedDevice?.id, populateFormFromProduct]
	);

	const handleOnErrorGetProducts = useCallback((error: ApolloError) => {
		console.log("handleOnErrorGetProducts Error: ", error);
	}, []);

	const { loading: loadingProducts } = useGetProductsByOrganizationIdQuery({
		skip: !detectedDevice?.organization_id,
		variables: {
			organization_id: detectedDevice?.organization_id
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onCompleted: handleOnCompleteGetProducts,
		onError: handleOnErrorGetProducts
	});

	const handleOnSelectProduct = useCallback(
		value => {
			console.log("handleOnSelectProduct value: ", value);

			if (value) {
				// Product selected - populate from product
				const objectValue = JSON.parse(value);
				setSelectedProduct(objectValue);
				populateFormFromProduct(objectValue);
			} else {
				// Selection cleared - revert to detected device if available
				setSelectedProduct(null);
				if (detectedDevice) {
					populateFormFromDetectedDevice(detectedDevice);
				}
			}
		},
		[detectedDevice, populateFormFromProduct, populateFormFromDetectedDevice]
	);

	// Preload values when the modal is opened
	useEffect(() => {
		if (open) {
			if (existingProduct) {
				// Edit mode: load existing product
				populateFormFromProduct(existingProduct);
			} else if (detectedDevice && !detectedDevice.id) {
				// Create mode: load detected device
				populateFormFromDetectedDevice(detectedDevice);
			}
		} else {
			// Clean when closed
			resetForm();
		}
	}, [open, existingProduct, detectedDevice, populateFormFromProduct, populateFormFromDetectedDevice, resetForm]);

	const handleOnCloseAttachmentModal = useCallback(() => {
		setAddAttachmentModalOpen(false);
		setAddAttachmentModalEditMode(false);
		clearSelectedAttachments();
	}, []);

	const handleOnRemoveAttachment = useCallback(
		(attachmentFile: any) => {
			// Use uid for local files, or the appropriate link for DB files
			const identifier =
				attachmentFile.uid ||
				attachmentFile.photo_link ||
				attachmentFile.video_link ||
				attachmentFile.document_link ||
				attachmentFile.audio_link;

			const newList = formState.attachmentsFiles.filter(element => {
				const elementId =
					element.uid || element.photo_link || element.video_link || element.document_link || element.audio_link;
				return elementId !== identifier;
			});
			updateFormState({ attachmentsFiles: newList });
		},
		[formState.attachmentsFiles, updateFormState]
	);

	// Handle removing existing attachments (for edit mode)
	const handleOnRemoveExistingAttachment = useCallback(
		(attachmentFile: any) => {
			const { id } = attachmentFile;
			if (id && isEditMode) {
				deleteAttachmentByIdMutation({
					variables: { id }
				});
			}
			handleOnRemoveAttachment(attachmentFile);
		},
		[isEditMode, deleteAttachmentByIdMutation, handleOnRemoveAttachment]
	);

	const handleOnEditAttachment = useCallback(
		(attachmentFile: any) => {
			updateSelectedAttachments(attachmentFile);
			setAddAttachmentModalEditMode(true);
			setAddAttachmentModalOpen(true);
		},
		[updateSelectedAttachments]
	);

	const updateAttachments = useCallback((attachmentFile: any) => {
		const { uid, name, file, type } = attachmentFile;

		const newAttachment = {
			uid,
			name,
			type,
			file,
			mime_type: file.type,
			originFileObj: file.originFileObj,
			localUrl: URL.createObjectURL(file.originFileObj)
		};

		setFormState(prev => {
			const filteredList = prev.attachmentsFiles.filter(element => element.uid !== uid);
			return {
				...prev,
				attachmentsFiles: [...filteredList, newAttachment]
			};
		});
	}, []);

	const handleOnConfirmPhoto = useCallback(
		(file: any, url: string) => {
			updateFormState({
				productPhotoThumbUrl: url,
				productPhotoFile: file
			});
		},
		[updateFormState]
	);

	const handleOnRemovePhoto = useCallback(() => {
		updateFormState({
			productPhotoThumbUrl: "",
			productPhotoFile: null
		});
	}, [updateFormState]);

	// Handle update for existing product
	const handleUpdateProduct = useCallback(async () => {
		if (!existingProduct) return;

		setUploading(true);

		try {
			// 1. Upload new photo if changed
			let photoLink = formState.productPhotoThumbUrl ? formState.productPhotoThumbUrl : null;
			if (formState.productPhotoFile) {
				const bucketName = 'product-photos';
				const fileName = createFirebaseFileName(formState.productPhotoFile, "product-photo", existingProduct.id);
				const { publicUrl } = await uploadToStorage(formState.productPhotoFile, () => {}, `${bucketName}/${fileName}`);
				photoLink = publicUrl;
			}

			// 2. Upload new attachments in parallel
			const newAttachments = formState.attachmentsFiles.filter(att => att.uid && !att.id);
			let attachmentsToUpload: Product_Attachments_Insert_Input[] = [];

			if (newAttachments.length > 0) {
				// Map each attachment to an upload promise
				const uploadPromises = newAttachments.map(async attachment => {
					const { file, name, type } = attachment;
					const mimeType = file.type;
					const mediaType = mimeType.split("/")[0];

					// Determine bucket path based on type
					let bucketSubPath = "documents";
					let linkField: "document_link" | "photo_link" | "video_link" | "audio_link" = "document_link";

					if (mediaType === "image") {
						bucketSubPath = "photos";
						linkField = "photo_link";
					} else if (mediaType === "video") {
						bucketSubPath = "videos";
						linkField = "video_link";
					} else if (mediaType === "audio") {
						bucketSubPath = "audios";
						linkField = "audio_link";
					}

					// Upload file
					const bucketName = `product-attachments/${bucketSubPath}`;
					const fileName = createFirebaseFileName(file, type || "attachment", name);
					const { publicUrl } = await uploadToStorage(file, () => {}, `${bucketName}/${fileName}`);

					// Return attachment object for database
					return {
						product_id: existingProduct.id,
						name,
						type,
						mime_type: mimeType,
						[linkField]: publicUrl
					} as Product_Attachments_Insert_Input;
				});

				// Wait for all uploads to complete
				const uploadResults = await Promise.allSettled(uploadPromises);

				// Filter only successful uploads
				attachmentsToUpload = uploadResults
					.filter(result => result.status === "fulfilled")
					.map(result => (result as PromiseFulfilledResult<Product_Attachments_Insert_Input>).value);
			}

			// 3. Insert new attachments if any
			if (attachmentsToUpload.length > 0) {
				await addProductAttachmentsMutation({
					variables: { objects: attachmentsToUpload }
				});
			}

			// 4. Update product
			await updateProductByIdMutation({
				variables: {
					id: existingProduct.id,
					set: {
						name: formState.name,
						description: formState.description,
						product_notes: formState.productNotes,
						sku: formState.sku,
						make: formState.make,
						model: formState.model,
						retail_price: formState.retailPrice,
						photo_link: photoLink,
						type: formState.type?.name
					}
				}
			});

			setUploading(false);
		} catch (error) {
			console.error("Error updating product:", error);
			setUploading(false);
			message.error("Failed to update product");
		}
	}, [existingProduct, formState, updateProductByIdMutation, addProductAttachmentsMutation]);

	const handleOnSubmit = useCallback(async () => {
		const {
			name,
			make,
			model,
			type,
			description,
			sku,
			retailPrice,
			productNotes,
			attachmentsFiles,
			productPhotoThumbUrl,
			productPhotoFile
		} = formState;

		if (!name || !make || !model || !type || !description) {
			message.warning("Please fill in all required fields (Name, Make, Model, Description, Type)");
			return;
		}

		if (isEditMode) {
			// Edit mode: update existing product
			await handleUpdateProduct();
		} else {
			// Create mode: update local detection state
			const input: Partial<DetectedDevice> = {
				name,
				description,
				sku,
				type,
				model,
				make,
				retail_price: retailPrice,
				product_notes: productNotes,
				attachments: attachmentsFiles.length > 0 ? attachmentsFiles : undefined,
				photo_link: productPhotoThumbUrl || undefined,
				productPhotoFile: productPhotoFile || undefined
			};

			setUploading(true);

			try {
				if (detectedDevice?.draft_product_id && onUpdateProduct) {
					await onUpdateProduct(detectedDevice.draft_product_id, input);
				} else if (onAddProduct) {
					await onAddProduct(input);
				}

				message.success("Product saved successfully");
				resetForm();
				if (onSuccess) {
					onSuccess();
				}
				onCancel();
			} catch (error) {
				console.error("Error saving product:", error);
				message.error("Failed to save product");
			} finally {
				setUploading(false);
			}
		}
	}, [formState, isEditMode, handleUpdateProduct, detectedDevice, onUpdateProduct, onAddProduct, resetForm, onSuccess, onCancel]);

	// Memoize loading state
	const isLoading = useMemo(
		() => uploading || loadingProducts || loadingUpdate || loadingAddAttachment || loadingDeleteAttachment,
		[uploading, loadingProducts, loadingUpdate, loadingAddAttachment, loadingDeleteAttachment]
	);

	// Destructure form state for cleaner JSX
	const {
		name,
		description,
		productNotes,
		sku,
		type,
		model,
		make,
		retailPrice,
		productPhotoThumbUrl,
		attachmentsFiles
	} = formState;

	return (
		<>
			<Modal
				open={open}
				onCancel={() => {
					resetForm();
					onCancel();
				}}
				footer={null}
				width={800}
				className="device-detection-product-modal"
				title={isEditMode ? "Edit Product" : detectedDevice ? "Edit Device" : "Add Device"}
			>
				{!isEditMode && detectedDevice?.organization_id && (
					<Form.Item className="new-product-modal-form-item-pair">
						<Select
							value={selectedProduct ? JSON.stringify(selectedProduct) : undefined}
							defaultActiveFirstOption={false}
							className="add-product-input-select"
							onChange={handleOnSelectProduct}
							options={productOptions}
							placeholder="Select Existing Device (Optional)"
							allowClear
							loading={loadingProducts}
							disabled={isLoading}
							suffixIcon={
								<img
									src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
									alt="dropdown icon"
									className="chevron-button"
								/>
							}
						/>
					</Form.Item>
				)}

				<span className="add-product-form-title">{"Device Details"}</span>
				<Form layout="vertical" onFinish={handleOnSubmit}>
					<Form.Item
						className="new-product-modal-form-item-pair"
						rules={[
							{
								required: true,
								message: "Please input the name of the Device."
							}
						]}
						required
						validateTrigger="onBlur"
					>
						<Input
							name="name"
							placeholder="Device Name"
							value={name}
							autoComplete="off"
							onChange={e => updateFormState({ name: e.target.value })}
							allowClear
							disabled={isLoading || !!selectedProduct}
						/>
					</Form.Item>

					<div className="two-inputs-container">
						<Form.Item className="new-product-modal-form-item-pair">
							<Input
								name="make"
								placeholder="Make"
								className="add-product-input-pair"
								value={make}
								autoComplete="off"
								onChange={e => updateFormState({ make: e.target.value })}
								allowClear
								disabled={isLoading || !!selectedProduct}
							/>
						</Form.Item>

						<Form.Item className="new-product-modal-form-item-pair">
							<Input
								name="model"
								placeholder="Model"
								className="add-product-input-pair"
								value={model}
								autoComplete="off"
								onChange={e => updateFormState({ model: e.target.value })}
								allowClear
								disabled={isLoading || !!selectedProduct}
							/>
						</Form.Item>
					</div>

					<div className="two-inputs-container">
						<Form.Item className="new-product-modal-form-item-pair">
							<Input
								name="sku"
								placeholder="SKU (optional)"
								className="add-product-input-pair"
								value={sku}
								autoComplete="off"
								onChange={e => updateFormState({ sku: e.target.value })}
								allowClear
								disabled={isLoading || !!selectedProduct}
							/>
						</Form.Item>

						<Form.Item className="new-product-modal-form-item-pair">
							<Select
								value={type?.name}
								className="add-product-input-select"
								onChange={value => {
									const category = getCategoryFromName(value);
									updateFormState({ type: category });
								}}
								options={categoryOptions}
								placeholder="Device Type"
								allowClear
								onClear={() => updateFormState({ type: null })}
								disabled={isLoading || (!!selectedProduct && !typeNotInMap)}
								suffixIcon={
									<img
										src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
										alt="dropdown icon"
										className="chevron-button"
									/>
								}
							/>
						</Form.Item>
					</div>

					<Form.Item className="new-product-modal-form-item-pair">
						<InputNumber
							name="retailPrice"
							placeholder="Retail Price (optional)"
							className="add-product-input-pair"
							value={retailPrice}
							disabled={isLoading || !!selectedProduct}
							onChange={value => updateFormState({ retailPrice: value })}
						/>
					</Form.Item>

					<Form.Item className="new-product-modal-form-item">
						<TextArea
							name="description"
							placeholder="Description"
							style={{ height: "148px", marginBottom: "21px" }}
							value={description}
							autoComplete="off"
							onChange={e => updateFormState({ description: e.target.value })}
							allowClear
							disabled={isLoading || !!selectedProduct}
						/>
					</Form.Item>

					<Form.Item className="new-product-modal-form-item">
						<TextArea
							name="product-notes"
							placeholder="Device Notes (optional)"
							style={{ height: "137px", marginBottom: "21px" }}
							value={productNotes}
							autoComplete="off"
							onChange={e => updateFormState({ productNotes: e.target.value })}
							allowClear
							disabled={isLoading || !!selectedProduct}
						/>
					</Form.Item>

					<Button
						className="upload-product-button"
						onClick={() => setAddProductPhotoModalOpen(true)}
						disabled={isLoading || !!selectedProduct}
					>
						{productPhotoThumbUrl ? "Replace Device Image" : "Upload Device Image"}
					</Button>

					{productPhotoThumbUrl ? (
						<div className="organization-button-container">
							<Image width={170} src={productPhotoThumbUrl} />{" "}
							<img
								alt={`remove-organization-photo-icon`}
								src={`${process.env.PUBLIC_URL}/assets/images/icons/remove-trash.svg`}
								className="organization-photo-action-icon"
								onClick={() => {
									if (isLoading || !!selectedProduct) {
										return;
									}
									handleOnRemovePhoto();
								}}
							/>
						</div>
					) : null}

					<UploadPhotoModal
						open={addProductPhotoModalOpen}
						onCancel={() => setAddProductPhotoModalOpen(false)}
						uploadMethod={handleOnConfirmPhoto}
						disableCrop
					/>
				</Form>

				<div className="add-product-form">
					<span className="add-product-form-title">{"Device Resources (optional)"}</span>

					<div className="add-product-documents">
						<Button
							className="upload-product-button"
							onClick={() => setAddAttachmentModalOpen(true)}
							disabled={isLoading || !!selectedProduct}
						>
							Add Resources
						</Button>
					</div>

					{attachmentsFiles && attachmentsFiles.length > 0 ? (
						<div className="attachments-container ">
							{attachmentsFiles.map((attachmentFile, index) => {
								const uid = attachmentFile.uid || attachmentFile.id || index;
								const { name, file, type, photo_link, video_link, document_link } = attachmentFile;

								return (
									<AttachmentItem
										key={`product-attachment-item-${uid}`}
										name={name}
										file={file}
										type={type}
										fileUrl={photo_link || video_link || document_link}
										editMode={!!selectedProduct}
										showEditButton={!selectedProduct}
										handleOnClick={() => {
											if (onClickAttachment) {
												onClickAttachment(attachmentFile);
											}
										}}
										onRemove={() => {
											if (isLoading || !!selectedProduct) {
												return;
											}

											if (isEditMode && attachmentFile.id) {
												handleOnRemoveExistingAttachment(attachmentFile);
											} else {
												handleOnRemoveAttachment(attachmentFile);
											}
										}}
										onEdit={() => {
											if (isLoading || !!selectedProduct) {
												return;
											}

											handleOnEditAttachment(attachmentFile);
										}}
									/>
								);
							})}
						</div>
					) : null}
				</div>

				<div className="add-product-button-container">
					<Button
						className="admin-add-product-button"
						htmlType="submit"
						onClick={handleOnSubmit}
						disabled={isLoading || !name || !make || !model || !type || !description}
						loading={isLoading}
					>
						{isEditMode ? "Update" : "Save"}
					</Button>
				</div>
			</Modal>

			<AddAttachmentModal
				open={addAttachmentModalOpen}
				onCancel={handleOnCloseAttachmentModal}
				updateAttachments={updateAttachments}
				editMode={addAttachmentModalEditMode}
				editProduct={false}
			/>
		</>
	);
};

export default NewProductModal;
